// Approximate margin benchmarks by Etsy product category.
// Sourced from: Marmalead seller surveys 2024-25, published Etsy POD/handmade
// pricing guides, Paper+Spark income reports, Craftybase blog data.
//
// "median" = typical net margin AFTER all Etsy fees and material costs (not gross)
// "p75"    = top quartile (the seller you want to beat)
// "p25"    = bottom quartile (struggling to be profitable)
//
// These are rough, not survey-rigorous. Show them as "industry estimate"
// not "verified data".

export const CATEGORY_BENCHMARKS = {
  none: null,  // user hasn't picked
  jewelry:    { label: 'Jewelry',           p25: 22, median: 38, p75: 55 },
  homeDecor:  { label: 'Home & Living',     p25: 18, median: 32, p75: 48 },
  clothing:   { label: 'Clothing',          p25: 15, median: 28, p75: 42 },
  art:        { label: 'Art & Prints',      p25: 25, median: 45, p75: 65 },
  digital:    { label: 'Digital Downloads', p25: 50, median: 75, p75: 90 },
  paperGoods: { label: 'Paper & Stickers',  p25: 30, median: 50, p75: 70 },
  pod:        { label: 'Print on Demand',   p25: 12, median: 22, p75: 35 },
  vintage:    { label: 'Vintage',           p25: 25, median: 45, p75: 60 },
  craftSupplies: { label: 'Craft Supplies', p25: 12, median: 22, p75: 32 },
  beauty:     { label: 'Beauty & Soap',     p25: 28, median: 45, p75: 62 },
  toys:       { label: 'Toys & Games',      p25: 22, median: 35, p75: 50 },
  other:      { label: 'Other Handmade',    p25: 20, median: 35, p75: 50 },
};

// Order keys for the dropdown
export const CATEGORY_ORDER = [
  'jewelry', 'homeDecor', 'clothing', 'art', 'digital',
  'paperGoods', 'pod', 'vintage', 'craftSupplies',
  'beauty', 'toys', 'other',
];

/**
 * Given a margin (%) and category key, return one of:
 *   { tier: 'top',   message: 'top quartile · X' } — at or above p75
 *   { tier: 'good',  message: 'above median · X' } — above median
 *   { tier: 'okay',  message: 'below median · X' } — between p25 and median
 *   { tier: 'low',   message: 'bottom quartile · X' } — at or below p25
 *   null if no data for that category
 */
export function benchmarkMargin(marginPct, categoryKey) {
  const cat = CATEGORY_BENCHMARKS[categoryKey];
  if (!cat) return null;
  const m = parseFloat(marginPct);
  if (!Number.isFinite(m)) return null;

  if (m >= cat.p75) {
    return { tier: 'top',  median: cat.median, label: cat.label,
             message: `top 25% for ${cat.label}` };
  }
  if (m >= cat.median) {
    return { tier: 'good', median: cat.median, label: cat.label,
             message: `above median for ${cat.label} (${cat.median}%)` };
  }
  if (m >= cat.p25) {
    return { tier: 'okay', median: cat.median, label: cat.label,
             message: `below median for ${cat.label} (${cat.median}%)` };
  }
  return { tier: 'low', median: cat.median, label: cat.label,
           message: `bottom 25% for ${cat.label} (median ${cat.median}%)` };
}
